import type {
  ApprovalGateId,
  PurchaseCostObjectType,
  PurchaseSpendClass,
  PurchasingAuthorityBand,
  PurchasingPaymentMethod,
  PurchasingPolicy,
} from '../types/purchasing';
import { DEFAULT_HOLD_REASONS, parseHoldReasons } from './purchasingHolds';

export const PURCHASING_POLICY_DOC_ID = 'purchasingPolicy';

export const APPROVAL_GATE_LABELS: Record<ApprovalGateId, string> = {
  supervisor: 'Supervisor',
  project_manager: 'Project Manager',
  department_head: 'Department Head',
  controller: 'Controller',
  executive: 'Executive',
};

export const APPROVAL_GATE_IDS: readonly ApprovalGateId[] = [
  'supervisor',
  'project_manager',
  'department_head',
  'controller',
  'executive',
];

/** Matches the shed-calculator default (Williamson County, TN). */
export const DEFAULT_SALES_TAX_PCT = 9.25;

export const PROJECT_CARD_PAYMENT_METHOD: PurchasingPaymentMethod = {
  id: 'project_card',
  label: 'Project Card',
};

export const DEFAULT_PAYMENT_METHODS: PurchasingPaymentMethod[] = [
  { ...PROJECT_CARD_PAYMENT_METHOD },
  { id: 'personal_card', label: 'Personal card' },
  { id: 'company_card', label: 'Company Card' },
  { id: 'company_check', label: 'Company check' },
  { id: 'ach_office', label: 'Bill Payment' },
  { id: 'other', label: 'Other' },
];

export const OTHER_QUOTE_PAYMENT_METHODS: PurchasingPaymentMethod[] = [
  { id: 'company_card', label: 'Company Card' },
  { id: 'ach_office', label: 'Bill Payment' },
];

function paymentMethodParts(
  method: { id?: string; label?: string } | string | null | undefined,
): { id: string; label: string } {
  if (!method) return { id: '', label: '' };
  if (typeof method === 'string') return { id: '', label: method };
  return { id: method.id || '', label: method.label || '' };
}

export function isProjectCardPayment(method: { id?: string; label?: string } | string | null | undefined): boolean {
  const { id, label } = paymentMethodParts(method);
  return id === 'project_card' || /project\s*card/i.test(label);
}

export function isCompanyCardPayment(method: { id?: string; label?: string } | string | null | undefined): boolean {
  const { id, label } = paymentMethodParts(method);
  return id === 'company_card' || /company\s*card/i.test(label);
}

export function isPersonalCardPayment(method: { id?: string; label?: string } | string | null | undefined): boolean {
  const { id, label } = paymentMethodParts(method);
  return id === 'personal_card' || /personal\s*card/i.test(label);
}

export function isAchPurchasePayment(method: { id?: string; label?: string } | string | null | undefined): boolean {
  const { id, label } = paymentMethodParts(method);
  return id === 'ach_office' || /\bach\b/i.test(label) || /bill\s*payment/i.test(label);
}

export function isCardPurchasePayment(method: { id?: string; label?: string } | string | null | undefined): boolean {
  return isProjectCardPayment(method) || isCompanyCardPayment(method) || isPersonalCardPayment(method);
}

export function resolvePaymentMethodId(
  method: { id?: string; label?: string } | string | null | undefined,
  methods: PurchasingPaymentMethod[] = DEFAULT_PAYMENT_METHODS,
): string | undefined {
  if (!method) return undefined;
  const { id, label } = paymentMethodParts(method);
  if (id) return id;
  if (isAchPurchasePayment(label)) return 'ach_office';
  if (isProjectCardPayment(label)) return 'project_card';
  if (isCompanyCardPayment(label)) return 'company_card';
  if (isPersonalCardPayment(label)) return 'personal_card';
  const match = methods.find(m => m.label === label || m.id === label);
  return match?.id;
}

export function resolvePaymentMethodLabel(
  method: { id?: string; label?: string } | string | null | undefined,
  methods: PurchasingPaymentMethod[] = DEFAULT_PAYMENT_METHODS,
): string {
  const { id, label } = paymentMethodParts(method);
  if (label.trim()) return label.trim();
  if (!id) return '';
  return methods.find(m => m.id === id)?.label || id;
}

/** Auto-issued POs charge the Sequence Project Card; keep that option even on older policies. */
export function withProjectCardPaymentMethod(methods: PurchasingPaymentMethod[]): PurchasingPaymentMethod[] {
  if (methods.some(isProjectCardPayment)) return methods;
  return [{ ...PROJECT_CARD_PAYMENT_METHOD }, ...methods];
}

export function defaultSelfIssuePaymentMethod(methods: PurchasingPaymentMethod[]): string {
  const withCard = withProjectCardPaymentMethod(methods);
  return withCard.find(isProjectCardPayment)?.label || withCard[0]?.label || '';
}

/** CapEx is owner / finance spend — never the ops supervisor → COO ladder. */
export const DEFAULT_CAPEX_GATES: ApprovalGateId[] = ['controller'];

export const DEFAULT_PURCHASING_POLICY: PurchasingPolicy = {
  bands: [
    {
      id: 'band-micro',
      minAmount: 0,
      maxAmount: 500,
      gates: ['project_manager'],
      overheadGates: ['supervisor'],
      capexGates: [...DEFAULT_CAPEX_GATES],
    },
    {
      id: 'band-standard',
      minAmount: 500,
      maxAmount: 2500,
      gates: ['supervisor', 'executive'],
      capexGates: [...DEFAULT_CAPEX_GATES],
    },
    {
      id: 'band-major',
      minAmount: 2500,
      maxAmount: null,
      gates: ['supervisor', 'project_manager', 'controller', 'executive'],
      capexGates: [...DEFAULT_CAPEX_GATES],
    },
  ],
  paymentMethods: DEFAULT_PAYMENT_METHODS.map(m => ({ ...m })),
  salesTaxPct: DEFAULT_SALES_TAX_PCT,
  holdReasons: DEFAULT_HOLD_REASONS.map(r => ({ ...r })),
};

function isGate(value: unknown): value is ApprovalGateId {
  return typeof value === 'string' && (APPROVAL_GATE_IDS as readonly string[]).includes(value);
}

function parseGates(raw: unknown): ApprovalGateId[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isGate);
}

function parseBand(raw: unknown, index: number): PurchasingAuthorityBand | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const minAmount = Number(row.minAmount);
  if (!Number.isFinite(minAmount) || minAmount < 0) return null;
  const maxRaw = row.maxAmount;
  const maxAmount =
    maxRaw === null || maxRaw === undefined || maxRaw === ''
      ? null
      : Number(maxRaw);
  if (maxAmount !== null && (!Number.isFinite(maxAmount) || maxAmount <= minAmount)) return null;
  const gates = parseGates(row.gates);
  if (gates.length === 0) return null;
  const overheadGates = parseGates(row.overheadGates);
  const capexGates = parseGates(row.capexGates);
  return {
    id: typeof row.id === 'string' && row.id.trim() ? row.id.trim() : `band-${index + 1}`,
    minAmount,
    maxAmount,
    gates,
    ...(overheadGates.length ? { overheadGates } : {}),
    ...(capexGates.length ? { capexGates } : {}),
  };
}

function parsePaymentMethods(raw: unknown): PurchasingPaymentMethod[] {
  if (!Array.isArray(raw)) return DEFAULT_PAYMENT_METHODS.map(m => ({ ...m }));
  const out: PurchasingPaymentMethod[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const id = typeof row.id === 'string' ? row.id.trim() : '';
    const label = typeof row.label === 'string' ? row.label.trim() : '';
    if (!id || !label || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label });
  }
  return out.length ? out : DEFAULT_PAYMENT_METHODS.map(m => ({ ...m }));
}

function clonePolicy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function parsePurchasingPolicy(raw: Record<string, unknown> | undefined): PurchasingPolicy {
  if (!raw) return clonePolicy(DEFAULT_PURCHASING_POLICY);
  const bands = Array.isArray(raw.bands)
    ? raw.bands.map(parseBand).filter((b): b is PurchasingAuthorityBand => !!b)
    : [];
  return {
    bands: bands.length ? bands : clonePolicy(DEFAULT_PURCHASING_POLICY.bands),
    paymentMethods: parsePaymentMethods(raw.paymentMethods),
    fallbackApproverUserId:
      typeof raw.fallbackApproverUserId === 'string' && raw.fallbackApproverUserId.trim()
        ? raw.fallbackApproverUserId.trim()
        : undefined,
    salesTaxPct: parseSalesTaxPct(raw.salesTaxPct),
    holdReasons: parseHoldReasons(raw.holdReasons),
  };
}

function parseSalesTaxPct(raw: unknown): number {
  if (raw === undefined || raw === null || raw === '') return DEFAULT_SALES_TAX_PCT;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 100) return DEFAULT_SALES_TAX_PCT;
  return Math.round(n * 1000) / 1000;
}

export function formatBandLabel(band: PurchasingAuthorityBand): string {
  const money = (n: number) =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  if (band.maxAmount == null) return `${money(band.minAmount)}+`;
  return `${money(band.minAmount)} – ${money(band.maxAmount)}`;
}

/** Inclusive min, exclusive max. Last open-ended band catches the rest. */
export function matchPurchasingBand(
  policy: PurchasingPolicy,
  amount: number,
): PurchasingAuthorityBand | null {
  if (!Number.isFinite(amount) || amount < 0) return null;
  const sorted = policy.bands.slice().sort((a, b) => a.minAmount - b.minAmount);
  for (const band of sorted) {
    if (amount < band.minAmount) continue;
    if (band.maxAmount == null || amount < band.maxAmount) return band;
  }
  return sorted[sorted.length - 1] ?? null;
}

export function gatesForBand(
  band: PurchasingAuthorityBand,
  costType: PurchaseCostObjectType,
  spendClass?: PurchaseSpendClass,
): ApprovalGateId[] {
  if (spendClass === 'capex') {
    return band.capexGates?.length ? band.capexGates : [...DEFAULT_CAPEX_GATES];
  }
  if (costType === 'overhead' && band.overheadGates?.length) return band.overheadGates;
  return band.gates;
}

